import {
    ApiBlueprint,
    createApiFactory,
    createFrontendPlugin,
    discoveryApiRef,
    ExtensionDefinition,
    FrontendPlugin,
    identityApiRef,
} from "@backstage/frontend-plugin-api";
import { EntityCardBlueprint } from "@backstage/plugin-catalog-react/alpha";
import { IncidentClient } from "@internal/backstage-plugin-incidentio-common";
import { incidentApiRef } from "@internal/backstage-plugin-incidentio-react";
import { RiFireFill } from "@remixicon/react";

/**
 * Registers the {@link incidentApiRef} API factory, providing an
 * {@link IncidentClient} backed by the Backstage discovery and identity APIs.
 *
 * This is the only place the client is constructed. The cards reach incident.io
 * through the `incidentio` backend and nowhere else: there is no proxy route for
 * them to call, and the API key is not visible to the browser.
 */
const incidentApi: ExtensionDefinition = ApiBlueprint.make({
    params: defineParams =>
        defineParams(
            createApiFactory({
                api: incidentApiRef,
                deps: {
                    discoveryApi: discoveryApiRef,
                    identityApi: identityApiRef,
                },
                factory: ({ discoveryApi, identityApi }) =>
                    new IncidentClient({ discoveryApi, identityApi }),
            })
        ),
});

/**
 * On-call card for Group/team entity pages: who is on-call for each schedule the
 * team's escalation path pages, plus the team's incidents/alerts. Gated to Group
 * entities in code, so no config filter is needed in config/extensions.yaml.
 */
const incidentOnCallCard = EntityCardBlueprint.make({
    name: "incident-oncall",
    params: {
        filter: (entity: { kind: string }) =>
            entity.kind.toLowerCase() === "group",
        loader: () =>
            import("@internal/backstage-plugin-incidentio-react").then(m => (
                <m.IncidentOnCallCard />
            )),
    },
});

/**
 * Consolidated card for Component/service entity pages: the component's owner-team
 * on-call plus the component's incidents/alerts, under one incident.io header.
 * Gated to Component entities in code.
 */
const incidentComponentCard = EntityCardBlueprint.make({
    name: "incident-component",
    params: {
        filter: (entity: { kind: string }) =>
            entity.kind.toLowerCase() === "component",
        loader: () =>
            import("@internal/backstage-plugin-incidentio-react").then(m => (
                <m.IncidentComponentCard />
            )),
    },
});

/**
 * The incidentio plugin: custom incident.io BUI cards that replace the stock
 * `@incident-io/backstage` Material UI cards on Group and Component entity pages.
 */
export const incidentioPlugin: FrontendPlugin = createFrontendPlugin({
    pluginId: "incidentio",
    title: "incident.io",
    icon: <RiFireFill />,
    extensions: [incidentApi, incidentOnCallCard, incidentComponentCard],
});

export default incidentioPlugin;
